// Delayed ("wait N minutes") automation rules, expressed as a preset trigger instead of a raw
// event + conditions. Each trigger maps to the rule's event_name plus the conditions the backend
// uses to measure the wait (see AutomationRulePendingExecution).

export const MIN_DELAY_MINUTES = 1;
export const MAX_DELAY_MINUTES = 43200; // 30 days
export const DEFAULT_WAIT_STATUS = 'pending';
export const WAIT_STATUSES = ['open', 'pending', 'snoozed', 'resolved'];

export const DELAYED_TRIGGERS = [
  { key: 'conversation_status', eventName: 'conversation_updated' },
  // Our unified message_type labels private notes 'private', so 'outgoing' already excludes them.
  {
    key: 'customer_unresponsive',
    eventName: 'message_created',
    messageType: 'outgoing',
  },
  {
    key: 'agent_unresponsive',
    eventName: 'message_created',
    messageType: 'incoming',
  },
];
export const DEFAULT_TRIGGER = DELAYED_TRIGGERS[0].key;

export const findTrigger = key =>
  DELAYED_TRIGGERS.find(trigger => trigger.key === key) || DELAYED_TRIGGERS[0];

// The one condition each trigger sets itself; everything else (inbox included) is an ordinary
// condition in the condition editor.
export const managedAttributeKey = triggerKey =>
  findTrigger(triggerKey).messageType ? 'message_type' : 'status';

// Status waits measure from the last status change, so their conditions may only use attributes
// already fixed when the status changes, or ones checked only when the wait is due (labels, working
// hours, agent replied, marketplace country). Mirrors AutomationRule::DELAYED_CONVERSATION_ATTRIBUTES
// and DELAYED_WHEN_DUE_ATTRIBUTES.
export const STATUS_WAIT_ATTRIBUTES = [
  'inbox_id',
  'channel_type',
  'browser_language',
  'referer',
  'mail_subject',
  'message_source_type',
  'call_direction',
  'country_code',
  'labels',
  'working_hours',
  'agent_replied',
  'marketplace_country',
];

// Message waits take any attribute except the message type they manage.
export const isWaitAttributeAllowed = (triggerKey, attributeKey) =>
  findTrigger(triggerKey).messageType
    ? attributeKey !== 'message_type'
    : STATUS_WAIT_ATTRIBUTES.includes(attributeKey);

const condition = (attributeKey, values) => ({
  attribute_key: attributeKey,
  filter_operator: 'equal_to',
  values,
  query_operator: 'and',
  custom_attribute_type: '',
});

// The condition a new row of the condition editor starts with: an attribute the wait allows.
export const defaultWaitCondition = triggerKey =>
  findTrigger(triggerKey).messageType
    ? {
        attribute_key: 'content',
        filter_operator: 'contains',
        values: '',
        query_operator: 'and',
        custom_attribute_type: '',
      }
    : {
        attribute_key: 'inbox_id',
        filter_operator: 'equal_to',
        values: '',
        query_operator: 'and',
        custom_attribute_type: '',
      };

// The managed condition in API format (values are arrays of ids).
export const buildWaitConditions = ({ trigger, status }) => {
  const { messageType } = findTrigger(trigger);
  return [
    messageType
      ? condition('message_type', [messageType])
      : condition('status', [status || DEFAULT_WAIT_STATUS]),
  ];
};

// Managed conditions first, then the extra ones, all joined with AND: the backend builds one flat
// query, so an OR would detach the extra conditions from the wait ("outgoing AND a OR b").
export const composeDelayedConditions = (
  waitConditions,
  extraConditions = []
) => {
  const conditions = [...waitConditions, ...extraConditions].map(item => ({
    ...item,
    query_operator: 'and',
  }));
  conditions[conditions.length - 1].query_operator = null;
  return conditions;
};

const valuesOf = item => {
  if (!item) return [];
  const values = Array.isArray(item.values) ? item.values : [item.values];
  return values
    .filter(value => value != null && value !== '')
    .map(value => (typeof value === 'object' ? value.id : value));
};

// Reads a saved delayed rule (API format) back into the wait controls. Only the first condition
// of the managed attribute is managed; the rest are extra conditions, returned by index so the
// caller can pick them from its already formatted condition list.
export const parseDelayedRule = rule => {
  if (!rule?.execution_delay) return null;

  const conditions = rule.conditions || [];
  const firstIndexOf = key =>
    conditions.findIndex(item => item.attribute_key === key);

  let trigger = DEFAULT_TRIGGER;
  if (rule.event_name === 'message_created') {
    const messageType = valuesOf(conditions[firstIndexOf('message_type')])[0];
    trigger =
      messageType === 'incoming'
        ? 'agent_unresponsive'
        : 'customer_unresponsive';
  }

  const managedIndex = firstIndexOf(managedAttributeKey(trigger));

  return {
    trigger,
    status:
      valuesOf(conditions[firstIndexOf('status')])[0] || DEFAULT_WAIT_STATUS,
    delay: rule.execution_delay,
    extraIndexes: conditions.flatMap((_, index) =>
      index === managedIndex ? [] : [index]
    ),
  };
};

// Largest whole unit that fits the delay, so a saved 1440 opens as "1 day".
export const unitForDelay = minutes => {
  if (minutes && minutes % (24 * 60) === 0) return 'days';
  if (minutes && minutes % 60 === 0) return 'hours';
  return 'minutes';
};

// "30 min", "4 h", "2 d" style parts for the rule list.
export const delayParts = minutes => {
  const unit = unitForDelay(minutes);
  if (unit === 'days') return { unit, count: minutes / (24 * 60) };
  if (unit === 'hours') return { unit, count: minutes / 60 };
  return { unit, count: minutes };
};
